// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { AppRoute } from '../../../app';
import { EmptyState, LoadingWrapper, Panel, TabPanel } from '../../../../common/components';
import PromotionService from '../../services/PromotionService';
import PromotionDetailsTab from './partials/PromotionDetailsTab';
import PromotionPeriodTab from './partials/PromotionPeriodTab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PromotionPeriod } from '../../types';

const TabIndex = {
  Details: 0,
  Period: 1,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  fullHeight: {
    flex: '1',
    height: '100%',
  },
}));

function PromotionDetailsPage() {
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Details);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const { promotionId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const queryClient = useQueryClient();

  const { data: promotionDetails, isLoading: promotionDetailsLoading } = useQuery(['promotionDetails', promotionId], async () => {
    const { data } = await PromotionService.fetchPromotion(promotionId);
    return data;
  });

  const updatePromotion = useMutation(
    async ({ promotion }: { promotion: Promotion }) => {
      return PromotionService.updatePromotion(promotionDetails.id, promotion);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['promotionDetails']);
        showAlert({
          title: t('common.success'),
          message: t('promotion.promotionUpdateSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('promotion.promotionUpdateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleUpdatePromotion = useCallback(
    async (promotion: Promotion) => {
      await updatePromotion.mutate({
        promotion
      });
    },
    [updatePromotion]
  );

  const deletePromotion = useMutation(
    async () => {
      return PromotionService.deletePromotion(promotionDetails.id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['promotionDetails']);
        showAlert({
          title: t('common.success'),
          message: t('promotion.promotionCloseSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('promotion.promotionCloseError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
      onSettled: () => {
        handleGoBack();
      }
    }
  );

  const handleDeletePromotion = useCallback(
    async () => {
      await deletePromotion.mutate();
    },
    [deletePromotion]
  );

  const createPromotionPeriod = useMutation(
    async ({ period }: { period: PromotionPeriod }) => {
      return PromotionService.createPromotionPeriod(promotionDetails.id, period);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['promotionDetails']);
        showAlert({
          title: t('common.success'),
          message: t('promotion.promotionPeriodCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('promotion.promotionPeriodCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleCreatePromotionPeriod = useCallback(
    async (period: PromotionPeriod) => {
      await createPromotionPeriod.mutate({
        period
      });
    },
    [createPromotionPeriod]
  );

  const handleGoBack = useCallback(() => {
    history.push(_.defaultTo(history.location.state?.from, AppRoute.Promotions));
  }, [history]);

  return (
    <Box id="PromotionDetails-container" className={classes.root}>
      <LoadingWrapper loading={promotionDetailsLoading}>
        <Panel
          id="PromotionDetails-panel"
          title={t('promotion.promotionDetails')}
          className={clsx(classes.panel, {
            [classes.fullHeight]: tabIndex !== TabIndex.Details,
          })}
        >
          {!promotionDetails ? (
            <EmptyState variant="unavailable" message={t('promotion.promotionDetailsNotAvailable')} />
          ) : (
            <>
              <Tabs
                id="PromotionDetails-tabs"
                className={classes.tabs}
                data-value={tabIndex}
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, index) => setTabIndex(index)}
              >
                <Tab
                  id="PromotionDetails-detailsTab"
                  label={t('promotion.promotion')}
                  data-index={TabIndex.Details}
                />
                <Tab
                  id="PromotionDetails-seriesTab"
                  label={t('promotion.promotionPeriods')}
                  data-index={TabIndex.Period}
                />
              </Tabs>
              <Box id="PromotionDetails-tabsContent" className={classes.fullHeight}>
                <TabPanel index={TabIndex.Details} currentIndex={tabIndex}>
                  <PromotionDetailsTab
                    promotion={promotionDetails}
                    products={[]}
                    onUpdatePromotion={handleUpdatePromotion}
                    onGoBack={handleGoBack}
                    actionsDisabled={promotionDetailsLoading}
                    onClosePromotion={handleDeletePromotion}
                  />
                </TabPanel>
                <TabPanel index={TabIndex.Period} currentIndex={tabIndex}>
                  <PromotionPeriodTab
                    promotionId={promotionDetails?.id}
                    periods={promotionDetails?.periods}
                    onCreatePeriod={handleCreatePromotionPeriod}
                    actionsDisabled={promotionDetailsLoading}
                  />
                </TabPanel>
              </Box>
            </>
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default PromotionDetailsPage;